import {
	applyHit,
	createPinataState,
	isSweetSpot,
	meterPosition,
	type HitEvent,
	type PinataRules,
	type PinataState,
} from './game.ts'

export type PinataStoreEvent =
	| { type: 'hit-result'; event: HitEvent; x: number; y: number }
	| { type: 'reset' }

// Phase 1 runs the rules locally. Phase 2 swaps swing() for a socket send and
// feeds server results through the same emit path.
export function createPinataStore(rules: PinataRules, now: () => number = () => performance.now()) {
	let state: PinataState = createPinataState(rules)
	let meterStartedAt = now()
	const listeners = new Set<(event: PinataStoreEvent) => void>()
	const emit = (event: PinataStoreEvent) => listeners.forEach((l) => l(event))

	return {
		getState: () => state,
		subscribe(listener: (event: PinataStoreEvent) => void) {
			listeners.add(listener)
			return () => void listeners.delete(listener)
		},
		/** Power meter marker, 0..1, right now */
		getMeter: () => meterPosition(now() - meterStartedAt),
		/**
		 * Swing at a point on the piñata (x/y in 0..1 space). In power swing mode
		 * the meter asks for a crit when the marker is in the green; the rules
		 * decide whether it was wound up enough to count.
		 */
		swing(x: number, y: number) {
			const at = now()
			const inGreen = isSweetSpot(meterPosition(at - meterStartedAt))
			const result = applyHit(state, {
				x,
				y,
				at,
				roll: Math.random(),
				strength: inGreen ? 'crit' : 'weak',
			})
			state = result.state
			emit({ type: 'hit-result', event: result.event, x, y })
			return result.event
		},
		reset() {
			state = createPinataState(rules)
			meterStartedAt = now()
			emit({ type: 'reset' })
		},
	}
}

export type PinataStore = ReturnType<typeof createPinataStore>
